import { Icon, Pressable } from '@rbilabs/universal-components';
import { usePaymentContext } from 'features/payment';
import { IPaymentMethod } from 'features/payment/types';
import { shouldReVaultPaymentMethod } from 'features/payment/utils';
import { StyleSheet, View } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { Typography } from 'components/ui';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { getDesignToken, primitive, spacing } from 'styles/constants';
import { Region } from 'utils/environment/types';

import { PaymentMethodDetails } from '../payment-method-details';
import { PaymentMethodIcon } from '../payment-method-icon';

import { styles as commonStyles } from './styles';
import { SelectionType } from './types';

type SelectorOptionProps = {
  option: IPaymentMethod;
  bordered?: boolean;
  selectedMethod: IPaymentMethod | undefined;
  onSelect: (type: SelectionType, id: string) => void;
  onUpdatePaymentMethod?: (id: string) => void;
};

export function SelectorOption({
  option,
  selectedMethod,
  onSelect,
  bordered,
}: SelectorOptionProps) {
  const { region } = useLocale();
  const isCARegion = region === Region.CA;
  const { isOrbital } = usePaymentContext();

  if (option.prepaid && isCARegion) {
    return null;
  }

  const accountIdentifier = option.accountIdentifier ?? option.fdAccountId ?? '';
  const isSelectedMethod =
    accountIdentifier === selectedMethod?.fdAccountId ||
    accountIdentifier === selectedMethod?.accountIdentifier;
  const { isInvalid = false, isExpired = false } = shouldReVaultPaymentMethod(option, isOrbital);
  const isRevaultRequired = isInvalid || isExpired;

  return (
    <Pressable
      accessible={!isRevaultRequired}
      accessibilityRole={isRevaultRequired ? undefined : 'button'}
      accessibilityState={isRevaultRequired ? undefined : { selected: isSelectedMethod }}
      testID={`method-${option.fdAccountId}`}
      style={[
        commonStyles.option,
        styles.option,
        {
          backgroundColor: isRevaultRequired ? primitive.$errorLight : undefined,
          borderBottomWidth: bordered ? 1 : 0,
        },
      ]}
      onPress={() => {
        if (isRevaultRequired) {
          return;
        }
        onSelect('selection', accountIdentifier);
      }}
    >
      <View style={styles.optionContent}>
        <View
          style={[
            commonStyles.row,
            { width: '100%', paddingVertical: spacing.$3, gap: spacing.$1 },
          ]}
        >
          <View style={[commonStyles.row, { flex: 1, flexWrap: 'wrap', gap: spacing.$4 }]}>
            <PaymentMethodIcon paymentMethod={option} size="md" bordered />
            <View style={styles.paymentMethodDetailsContainer}>
              <PaymentMethodDetails paymentMethod={option} displayFor="selectorOption" />
            </View>
          </View>
          {isSelectedMethod && (
            <Icon
              accessibilityHidden
              variant="check"
              title="check"
              color={getDesignToken('icon-default')}
              size={5}
            />
          )}
        </View>
        {isRevaultRequired && (
          <>
            <View style={styles.dashedSeparator} />
            <View style={[commonStyles.row, styles.expiredCardContainer]}>
              <View style={[commonStyles.row, { flex: 1 }]}>
                <Icon variant="warning" size="xs" />
                <Typography style={styles.expiredCardText}>{t('expiredCard')}</Typography>
              </View>
              <ActionButton
                onPress={() => onSelect('update', accountIdentifier)}
                variant={ActionButtonVariants.OUTLINE}
              >
                <Typography style={styles.updateButtonText}>{t('update')}</Typography>
              </ActionButton>
            </View>
          </>
        )}
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  option: {
    padding: 0,
    paddingHorizontal: spacing.$2,
  },
  optionContent: {
    flexDirection: 'column',
    flex: 1,
  },
  paymentMethodDetailsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    flex: 1,
    gap: spacing.$2,
  },
  expiredCardText: {
    fontFamily: primitive.fontFamily.$body,
    color: primitive.$bbqBrown,
    fontSize: 12,
    marginLeft: spacing.$2,
  },
  dashedSeparator: {
    flex: 1,
    borderWidth: 1,
    borderColor: primitive.$bbqBrownOpacity10,
    borderStyle: 'dashed',
  },
  expiredCardContainer: {
    gap: spacing.$1,
    flex: 1,
    width: '100%',
    paddingVertical: spacing.$2,
  },
  updateButtonText: {
    fontSize: 12,
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.$bbqBrown,
    paddingHorizontal: spacing.$6,
  },
});
